import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Search, Filter, DollarSign, Download, Eye, Calendar } from 'lucide-react';
import { commissionService } from '../api/commissions';
import { payoutService } from '../api/payouts';
import type { CommissionEntry } from '../types';
import toast from 'react-hot-toast';

export default function Commissions() {
  const { formatCurrency } = useCurrency();
  const [commissions, setCommissions] = useState<CommissionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    try {
      setLoading(true);
      const data = await commissionService.getAllEntries();
      setCommissions(data);
    } catch (error) {
      console.error('Failed to load commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const filteredCommissions = commissions.filter(commission => {
    const matchesSearch = commission.referrer.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         commission.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || commission.payoutStatus === statusFilter;
    const matchesKind = kindFilter === 'all' || commission.referrer.kind === kindFilter;
    return matchesSearch && matchesStatus && matchesKind;
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      unpaid: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const getKindBadge = (kind: string) => {
    const styles = {
      external_partner: 'bg-purple-100 text-purple-800',
      internal_staff: 'bg-blue-100 text-blue-800',
      stakeholder: 'bg-green-100 text-green-800',
    };
    return styles[kind as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const handleSelectCommission = (commissionId: string) => {
    setSelectedCommissions(prev => 
      prev.includes(commissionId)
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  const handleRecordPayout = async () => {
    if (selectedCommissions.length === 0) {
      toast.error('Please select commissions to pay out');
      return;
    }

    try {
      const selectedEntries = commissions.filter(c => selectedCommissions.includes(c.id));
      
      const referrerGroups = selectedEntries.reduce((groups, entry) => {
        const key = `${entry.referrer.kind}-${entry.referrer.id}`;
        if (!groups[key]) {
          groups[key] = {
            referrer: entry.referrer,
            entries: [],
            amount: 0,
          };
        }
        groups[key].entries.push(entry);
        groups[key].amount += entry.calculatedAmount;
        return groups;
      }, {} as Record<string, any>);

      for (const group of Object.values(referrerGroups)) {
        const payoutData = {
          referrer: group.referrer,
          amount: group.amount,
          method: 'cash' as const,
          commissionEntryIds: group.entries.map((e: any) => e.id),
        };
        await payoutService.create(payoutData);
      }

      for (const commissionId of selectedCommissions) {
        await commissionService.updateEntryStatus(commissionId, 'paid');
      }

      setSelectedCommissions([]);
      setShowPayoutModal(false);
      await loadCommissions();
      toast.success('Payouts recorded successfully');
    } catch (error) {
      console.error('Failed to record payouts:', error);
      toast.error('Failed to record payouts');
    }
  };

  const handleExportCSV = () => {
    const staffCommissions = filteredCommissions.filter(c => c.referrer.kind === 'internal_staff');
    
    if (staffCommissions.length === 0) {
      toast.error('No staff commissions to export');
      return;
    }

    const csvContent = [
      ['Employee ID', 'Name', 'Referral ID', 'Amount', 'Date', 'Status'].join(','),
      ...staffCommissions.map(commission => [
        commission.referrer.id,
        'Staff Member', // In real implementation, resolve staff name
        commission.referralId,
        commission.calculatedAmount.toFixed(2),
        new Date(commission.createdAt).toLocaleDateString(),
        commission.payoutStatus,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-commissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Staff commissions exported to CSV');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commission Ledger</h1>
          <p className="text-gray-600">Track and manage referral commissions</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExportCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => setShowPayoutModal(true)}
            disabled={selectedCommissions.length === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50"
          >
            <DollarSign className="w-4 h-4" />
            <span>Record Payout</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by referrer ID or reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="external_partner">Partners</option>
                <option value="internal_staff">Staff</option>
                <option value="stakeholder">Stakeholders</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCommissions(filteredCommissions.map(c => c.id));
                      } else {
                        setSelectedCommissions([]);
                      }
                    }}
                    checked={selectedCommissions.length === filteredCommissions.length && filteredCommissions.length > 0}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referrer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCommissions.map((commission) => (
                <tr key={commission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedCommissions.includes(commission.id)}
                      onChange={() => handleSelectCommission(commission.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {commission.referrer.id}
                    </div>
                    {commission.referrer.code && (
                      <div className="text-sm text-gray-500">
                        {commission.referrer.code}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getKindBadge(commission.referrer.kind)}`}>
                      {commission.referrer.kind.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${commission.calculatedAmount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(commission.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(commission.payoutStatus)}`}>
                      {commission.payoutStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCommissions.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No commissions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || kindFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Commissions will appear here when referrals are completed.'}
            </p>
          </div>
        )}
      </div>

      {/* Payout Confirmation Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Record Payout
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You are about to record payouts for {selectedCommissions.length} commission entries.
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPayoutModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayout}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Confirm Payout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
